import { StrictMode, ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { createStore, applyMiddleware, compose } from 'redux'
import createSagaMiddleware from 'redux-saga'
import { IntlProvider, createIntl } from 'react-intl'
import { Provider } from 'react-redux'
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import appReducer from './reducers'
import rootSaga from './sagas'
import bootstrapSaga from './sagas/bootstrap'
import initApis from './apis'
import ConnectedApp from './containers/ConnectedApp'
import messages from './intl/messages/en.json'
import { theme } from './styles/theme'
import { OidcProvider } from '@axa-fr/react-oidc'
import OpenIdStatusPage from './components/LandingPage/OpenIdStatusPage'
import AuthenticatingError from './components/LandingPage/AuthenticatingError'
import { defineMessages } from 'react-intl'
import fetchConfig from './apis/config'
import InvitationProvider from './components/InvitationProvider'
import { BrowserRouter as Router, useLocation } from 'react-router-dom'
import { LicenseInfo } from '@mui/x-license'
import { setupMatomo } from './helpers/matomo'
import { INVITATION_VERSION } from './helpers/search-params.const'
import { oidcScope } from './helpers/oidcScope'
import TokenSync from './components/TokenSync/TokenSync'
import SessionExpiredDialog from './components/SessionExpiredDialog'
import { prehydrateAccessTokenFromOidcStorage } from './actions/tokenBridge'
import { notifySessionExpired, markSigningOut } from './actions/sessionBridge'
import ErrorBoundary from './components/ErrorBoundary'
import { endAuthenticatedSession } from './apis/appInsights'

type OidcConfigurationCompat = {
  client_id: string
  authority: string
  redirect_uri: string
  silent_redirect_uri?: string
  scope: string
  storage?: Storage
  refresh_time_before_tokens_expiration_in_second?: number
  extras?: Record<string, string>
  service_worker_relative_url?: string
  service_worker_only?: boolean
}

if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then(rs => rs.forEach(r => r.unregister()))
}

const muiLicenseKey: string = import.meta.env.VITE_MUI_LICENSE_KEY
LicenseInfo.setLicenseKey(muiLicenseKey)

prehydrateAccessTokenFromOidcStorage()

const sagaMiddleware = createSagaMiddleware()

const composeEnhancer = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

const store = createStore(
  appReducer,
  composeEnhancer(applyMiddleware(sagaMiddleware))
)

sagaMiddleware.run(rootSaga)

export const intl = createIntl({ locale: 'en', messages: messages })

const localMessages = defineMessages({
  headline: {
    id: 'screens.openIdStatusPage.headline'
  },
  pleaseWait: {
    id: 'screens.openIdStatusPage.pleaseWait'
  },
  errorHeadline: {
    id: 'screens.openIdStatusPage.errorHeadline'
  },
  errorBody: {
    id: 'screens.openIdStatusPage.errorBody'
  },
  loading: {
    id: 'screens.openIdStatusPage.loading'
  },
  crashHeadline: {
    id: 'screens.errorBoundary.headline'
  },
  crashBody: {
    id: 'screens.errorBoundary.body'
  }
})

type IndexPropType = {
  openIdConfig: OidcConfigurationCompat
}

const onSessionLost = () => {
  endAuthenticatedSession()
  notifySessionExpired('expired')
}
const onLogoutFromAnotherTab = () => {
  endAuthenticatedSession()
  notifySessionExpired('signed-out')
}
const onLogoutFromSameTab = () => markSigningOut()

// Lives inside Router so it can reset the boundary when the route changes.
const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation()
  return (
    <ErrorBoundary
      resetKey={pathname}
      fallback={
        <OpenIdStatusPage
          headline={intl.formatMessage(localMessages.crashHeadline)}
          body={intl.formatMessage(localMessages.crashBody)}
        />
      }
    >
      {children}
    </ErrorBoundary>
  )
}

const Index = ({ openIdConfig }: IndexPropType) => (
  <StrictMode>
    <Provider store={store}>
      <IntlProvider locale="en" messages={messages}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <OidcProvider
              configuration={openIdConfig}
              onSessionLost={onSessionLost}
              onLogoutFromAnotherTab={onLogoutFromAnotherTab}
              onLogoutFromSameTab={onLogoutFromSameTab}
              onEvent={(configurationName, name, data) => {
                if (import.meta.env.DEV && name !== 'token_timer') {
                  // eslint-disable-next-line no-console
                  console.log('[OIDC event]', configurationName, name, data)
                }
              }}
              authenticatingComponent={() => (
                <OpenIdStatusPage
                  headline={intl.formatMessage(localMessages.headline)}
                  body={intl.formatMessage(localMessages.pleaseWait)}
                />
              )}
              callbackSuccessComponent={() => (
                <OpenIdStatusPage
                  headline={intl.formatMessage(localMessages.headline)}
                  body={intl.formatMessage(localMessages.pleaseWait)}
                />
              )}
              authenticatingErrorComponent={AuthenticatingError}
              loadingComponent={() => (
                <OpenIdStatusPage
                  headline={intl.formatMessage(localMessages.loading)}
                  body={intl.formatMessage(localMessages.pleaseWait)}
                />
              )}
            >
              <Router>
                <TokenSync />
                <SessionExpiredDialog />
                <RouteErrorBoundary>
                  <InvitationProvider>
                    <ConnectedApp />
                  </InvitationProvider>
                </RouteErrorBoundary>
              </Router>
            </OidcProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </IntlProvider>
    </Provider>
  </StrictMode>
)

fetchConfig().then(config => {
  const openIdConfig: OidcConfigurationCompat = {
    client_id: config.auth.clientName,
    authority: config.auth.url,
    redirect_uri: `${window.location.origin}/authentication/callback`,
    silent_redirect_uri: `${window.location.origin}/authentication/silent-callback`,
    scope: oidcScope(config.auth.url),
    storage: sessionStorage,
    refresh_time_before_tokens_expiration_in_second: 120,
    extras: {
      noTenant: window.location.search.includes(INVITATION_VERSION).toString()
    }
  }

  if (config.environment === 'prod') setupMatomo()

  // TEMPORARY (139305): exposed on DEV/TEST so QA can force an expired token.
  // One build ships to every environment, so gate on runtime config, not
  // import.meta.env. Revert to `import.meta.env.DEV` only once QA signs off.
  if (import.meta.env.DEV || ['dev', 'test'].includes(config.environment)) {
    import('./utils/devSessionTools')
  }

  sagaMiddleware
    .run(bootstrapSaga)
    .toPromise()
    .then(() => {
      initApis(config)
      const container = document.getElementById('root')
      if (container) {
        createRoot(container).render(<Index openIdConfig={openIdConfig} />)
      }
    })
    // eslint-disable-next-line no-console
    .catch(error => console.error(error))
})
